import React, { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Clock, Users, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import ImageWithLoader from './ImageWithLoader';
import PriceDisplay from './PriceDisplay';

const tours = [
  {
    id: 1,
    title: 'Walking Tour with Gaddafi Mosque',
    image: 'https://imgur.com/LTxFu0W.jpg',
    location: 'Kampala',
    duration: '3 hours',
    groupSize: '1-8 people',
    price: 32,
    description: 'Explore Kampala\'s iconic mosque and cultural sites.',
    tag: 'Most Popular'
  },
  {
    id: 2,
    title: 'Complete Kampala Experience',
    image: 'https://imgur.com/EzHnSsj.jpg',
    location: 'Kampala',
    duration: 'Full Day',
    groupSize: '1-8 people',
    price: 25,
    description: 'Comprehensive tour of Kampala\'s highlights.',
    tag: 'Best Value'
  },
  {
    id: 3,
    title: 'City Walking Food Tour',
    image: 'https://imgur.com/4kwMnNu.jpg',
    location: 'Kampala',
    duration: '4 hours',
    groupSize: '2-8 people',
    price: 77,
    description: 'Taste local delicacies and explore food markets.',
    tag: 'Food & Culture'
  },
  {
    id: 4,
    title: 'White Water Rafting',
    image: 'https://imgur.com/tzBxU2P.jpg',
    location: 'Jinja',
    duration: 'Full Day',
    groupSize: '2-8 people',
    price: 160,
    description: 'Experience thrilling Grade 5 rapids on the Nile.',
    tag: 'Water Adventure'
  },
  {
    id: 5,
    title: 'Bungee Jumping',
    image: 'https://imgur.com/Z97JH4P.jpg',
    location: 'Jinja',
    duration: '2 hours',
    groupSize: '1-10 people',
    price: 120,
    description: 'Take the plunge from a 44-meter platform over the Nile.',
    tag: 'Extreme Adventure'
  },
  {
    id: 6,
    title: 'Sunset Cruise',
    image: 'https://imgur.com/ZhSOWHi.jpg',
    location: 'Jinja',
    duration: '2 hours',
    groupSize: '2-20 people',
    price: 25,
    description: 'Relax on the Nile with drinks and stunning views.',
    tag: 'Relaxation'
  },
  {
    id: 7,
    title: 'Kayaking',
    image: 'https://imgur.com/Wa3J7KL.jpg',
    location: 'Jinja',
    duration: '3 hours',
    groupSize: '1-8 people',
    price: 50,
    description: 'Paddle along the Nile with guided tours.',
    tag: 'Water Sport'
  },
  {
    id: 8,
    title: 'Quad Biking',
    image: 'https://imgur.com/IsRyKcu.jpg',
    location: 'Jinja',
    duration: '2 hours',
    groupSize: '1-6 people',
    price: 60,
    description: 'Explore Jinja\'s scenic routes on a thrilling adventure.',
    tag: 'Land Adventure'
  },
  {
    id: 9,
    title: '1-Day Entebbe Experience',
    image: 'https://imgur.com/ELruAkr.jpg',
    location: 'Entebbe',
    duration: 'Full Day',
    groupSize: '1-8 people',
    price: 196,
    description: 'Explore Entebbe\'s attractions and Lake Victoria.',
    tag: 'Recommended'
  }
];

const CitySitingTours = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();

  const displayedTours = showAll ? tours : tours.slice(0, 6);

  return (
    <section className="py-12 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div className="relative">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {t('cityTours.title')}
              <span className="absolute -top-4 right-0 text-xs font-normal bg-ta-green text-white px-2 py-0.5 rounded-full transform -rotate-12">
                New
              </span>
            </h2>
            <div className="h-1 w-20 bg-ta-green rounded-full mb-2"></div>
            <p className="text-gray-600 dark:text-gray-300">{t('cityTours.subtitle')}</p>
          </div>
          <Link 
            to="/uganda-city-tours" 
            className="hidden md:flex items-center text-ta-green hover:text-ta-green-dark transition-colors group"
          >
            {t('common.seeAll')}
            <ChevronRight className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {displayedTours.map((tour, index) => (
            <div 
              key={tour.id}
              className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1"
              onMouseEnter={() => setHoveredCard(tour.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-40">
                <ImageWithLoader 
                  src={tour.image} 
                  alt={tour.title} 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-2 left-2">
                  <span className="inline-block bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm text-[10px] font-medium px-1.5 py-0.5 rounded-full dark:text-white">
                    {tour.tag}
                  </span>
                </div>
              </div>

              <div className="p-3">
                <h3 className="font-medium text-sm text-gray-900 dark:text-white line-clamp-1 group-hover:text-ta-green transition-colors">
                  {tour.title}
                </h3>
                
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <div className="flex items-center">
                    <MapPin className="h-3 w-3 text-ta-green" />
                    <span className="ml-0.5">{tour.location}</span>
                  </div>
                  <PriceDisplay price={tour.price} size="sm" className="text-ta-green" />
                </div>

                <Link
                  to="/uganda-city-tours"
                  className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <span className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs font-medium px-3 py-1.5 rounded-full transform scale-75 group-hover:scale-100 transition-transform duration-300">
                    {t('experiences.viewDetails')}
                  </span>
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <button 
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center text-ta-green hover:text-ta-green-dark transition-colors group"
          >
            {showAll ? t('common.viewLess') : t('common.viewMore')}
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-300 ${showAll ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CitySitingTours;